import {
  mapCategoryColorToIndicator,
  resolveCategoryColor
} from "@/helpers/behaviourCoreValueForm.js"
import behaviourAutomatedPointsConstants from "@/constants/behaviourAutomatedPointsConstants.js"

const { AUTOMATION_TYPE, AUTOMATED_CATEGORY_ID } =
  behaviourAutomatedPointsConstants

const DEFAULT_AUTOMATED_POSITIVE_BEHAVIOUR_NAMES = Object.freeze([
  "on time for school",
  "on time for class",
  "being on time for class"
])

export function isAutomatedPointsPositiveBehaviourRow(item, terminology = {}) {
  const automationType = String(
    item?.automationType ?? item?.automation_type ?? ""
  ).trim()
  if (
    automationType === AUTOMATION_TYPE.ON_TIME_FOR_SCHOOL ||
    automationType === AUTOMATION_TYPE.ON_TIME_FOR_CLASS
  ) {
    return true
  }

  const categoryId = Number(
    item?.automatedCategoryId ?? item?.automated_category_id
  )
  if (
    categoryId === AUTOMATED_CATEGORY_ID.ON_TIME_FOR_SCHOOL ||
    categoryId === AUTOMATED_CATEGORY_ID.ON_TIME_FOR_CLASS
  ) {
    return true
  }

  const name = String(item?.name ?? "")
    .trim()
    .toLowerCase()
  if (!name) return false

  const terminologyNames = [
    terminology?.onTimeForSchool,
    terminology?.onTimeForClass
  ]
    .map((label) =>
      String(label ?? "")
        .trim()
        .toLowerCase()
    )
    .filter(Boolean)

  if (terminologyNames.includes(name)) {
    return true
  }

  return DEFAULT_AUTOMATED_POSITIVE_BEHAVIOUR_NAMES.includes(name)
}

export function formatPositiveBehaviourOptionLabel(name, points) {
  const trimmedName = String(name ?? "").trim()
  if (!trimmedName) return ""

  const pointsValue = Number(points)
  if (points == null || points === "" || !Number.isFinite(pointsValue)) {
    return trimmedName
  }

  return `${trimmedName} (${pointsValue})`
}

/**
 * Maps API positive behaviour list item to table row shape.
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiPositiveBehaviourToRow(item) {
  const statusNum = Number(item?.status)
  const status = statusNum === 0 ? "inactive" : "active"
  const coreValue = item?.core_value || item?.coreValue || {}
  const coreValueCategory = String(
    coreValue?.category ?? item?.core_value_category ?? ""
  ).trim()
  const coreValueCategoryColor = resolveCategoryColor(coreValueCategory)

  return {
    id: item?.id,
    name: String(item?.name ?? ""),
    awardDescription: String(
      item?.description ??
        item?.award_description ??
        item?.awardDescription ??
        ""
    ),
    points: item?.points_awarded ?? item?.points ?? null,
    coreValueId: item?.core_value_id ?? coreValue?.id ?? null,
    coreValueName: String(coreValue?.name ?? item?.core_value_name ?? ""),
    coreValueCategory,
    coreValueCategoryColor,
    coreValueIndicatorColor: coreValueCategoryColor
      ? mapCategoryColorToIndicator(coreValueCategoryColor)
      : undefined,
    automationType: String(item?.automation_type ?? "").trim() || null,
    automatedCategoryId:
      item?.automated_category_id != null
        ? Number(item.automated_category_id)
        : null,
    sort_order: item?.sort_order ?? null,
    status
  }
}

/** Allowed values for the positive behaviours list `sort` query param. */
export const POSITIVE_BEHAVIOUR_SORT_FIELDS = Object.freeze([
  "name",
  "core_value_id",
  "award_description",
  "points_awarded",
  "status"
])

/** Allowed values for the positive behaviours list `order` query param. */
export const POSITIVE_BEHAVIOUR_SORT_ORDERS = Object.freeze(["asc", "desc"])

/**
 * Maps a row column key (table field) to the API `sort` value.
 * Returns `null` when the column is not sortable on the server.
 * @param {string|null|undefined} columnKey
 * @returns {string|null}
 */
export function mapPositiveBehaviourSortKey(columnKey) {
  switch (String(columnKey ?? "").trim()) {
    case "name":
      return "name"
    case "coreValueName":
      return "core_value_id"
    case "awardDescription":
      return "award_description"
    case "points":
      return "points_awarded"
    case "status":
      return "status"
    default:
      return null
  }
}

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   statusFilter?: string,
 *   coreValueId?: string | number | null,
 *   sortBy?: string|null,
 *   sortOrder?: string|null
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildPositiveBehavioursListParams({
  page = 1,
  perPage = 25,
  search = "",
  statusFilter = "all",
  coreValueId = null,
  sortBy = null,
  sortOrder = null
} = {}) {
  const params = {
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  if (statusFilter === "active") {
    params.status = 1
  } else if (statusFilter === "inactive") {
    params.status = 0
  }

  const coreValueRaw = String(coreValueId ?? "").trim()
  if (coreValueRaw && coreValueRaw !== "all") {
    const coreValueNumeric = Number(coreValueRaw)
    if (Number.isFinite(coreValueNumeric)) {
      params.core_value_id = coreValueNumeric
    }
  }

  const sortByValue = String(sortBy ?? "").trim()
  const sortOrderValue = String(sortOrder ?? "")
    .trim()
    .toLowerCase()
  if (
    POSITIVE_BEHAVIOUR_SORT_FIELDS.includes(sortByValue) &&
    POSITIVE_BEHAVIOUR_SORT_ORDERS.includes(sortOrderValue)
  ) {
    params.sort = sortByValue
    params.order = sortOrderValue
  }

  return params
}
